import { DatePipe } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { PatientEditRequestService as GeneratedPatientEditRequestService } from '@app/generated-api';
import { DateFormat } from '@models/date-format';
import { map } from 'rxjs';
import { UserServiceV2 } from '../user-service/user.service';
@Injectable({
    providedIn: 'root',
})
export class PatientEditRequestService {
    private readonly generatedPatientEditRequestService = inject(GeneratedPatientEditRequestService);
    private readonly userService = inject(UserServiceV2);
    private readonly datePipe = inject(DatePipe);
    private readonly hasPatientEditRequestsNotificationPermission =
        this.userService.signalPermissions.patient.editRequest.notificationSubmittedView;

    // this signal is used to delay the call to get the count of submitted patient edit requests until a component using it is initialized.
    // otherwise this would unnecessarily call the API on service created (first injection is in the appbar)
    // why this rxResource is created in the service then? - because the count is needed in multiple places (appbar notification and patients page), and this way we can share the result and avoid multiple calls to the API
    // also, any component that changed it can trigger .reload() and it will reload on all components using this resource
    public readonly submittedPatientEditRequestsCountInit = signal(false);
    public readonly submittedPatientEditRequestsCountResource = rxResource({
        params: () => {
            if (!this.submittedPatientEditRequestsCountInit() || !this.hasPatientEditRequestsNotificationPermission()) {
                return undefined; // will skip the call if user doesn't have permission
            }
            return {};
        },
        stream: () => {
            return this.generatedPatientEditRequestService.patientEditRequestGetSubmittedCount();
        },
    });

    private readonly hasPatientEditRequestsPermission = this.userService.signalPermissions.patient.editRequest.listView;

    // this signal is used to delay the call to get the list of submitted patient edit requests until a component using it is initialized.
    // otherwise this would unnecessarily call the API on service created (first injection is in the appbar)
    // why this rxResource is created in the service then? - because the count is needed in multiple places (appbar notification and patients page), and this way we can share the result and avoid multiple calls to the API
    // also, any component that changed it can trigger .reload() and it will reload on all components using this resource
    public readonly submittedPatientEditRequestsInit = signal(false);
    public readonly submittedPatientEditRequestsResource = rxResource({
        params: () => {
            if (!this.submittedPatientEditRequestsInit() || !this.hasPatientEditRequestsPermission()) {
                return undefined; // will skip the call if user doesn't have permission
            }
            return {};
        },
        stream: () => {
            const canViewPcsId = this.userService.signalPermissions.patient.pcsIdView();
            return this.generatedPatientEditRequestService.patientEditRequestGetSubmittedPatientEditRequests().pipe(
                map((data) =>
                    data.map((p) => ({
                        ...p,
                        pcsId: canViewPcsId ? p.pcsId : undefined,
                        submitDateFormatted:
                            this.datePipe
                                .transform(p.submitDate, DateFormat.dateTime12, undefined, 'en-US')
                                ?.toUpperCase() || '',
                    }))
                )
            );
        },
    });

    submittedPatientEditRequests$ = toObservable(this.submittedPatientEditRequestsCountResource.value).pipe(
        map((count) => count || 0)
    );
}
